import { Phone, Mail, MessageCircle, MapPin, Clock } from "lucide-react";
import Seo, { buildBreadcrumbLd, localBusinessLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import { company, whatsappUrl, regionHubs } from "../data/site";

const crumbs = [
  { label: "Startseite", to: "/" },
  { label: "Kontakt", to: "/kontakt" },
];

export default function Kontakt() {
  return (
    <div data-testid="page-kontakt">
      <Seo
        path="/kontakt"
        title="Kontakt | Entrümpelung anfragen – Rümpeltaxi Altenstadt, Ulm & Neu-Ulm"
        description="Anfrage an Rümpeltaxi in Altenstadt (Iller): telefonisch unter 0151 70004440, per WhatsApp oder Formular. Entrümpelung und Haushaltsauflösung in Ulm, Neu-Ulm, Illertissen und Region."
        jsonLd={[buildBreadcrumbLd(crumbs), localBusinessLd]}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero
        kicker="Kontakt"
        title="Beschreiben Sie uns Ihr Anliegen"
        lead="Wir melden uns persönlich bei Ihnen. Rufen Sie an, schreiben Sie per WhatsApp oder senden Sie das Formular – auf Wunsch mit Fotos für eine schnellere Einschätzung."
      />

      {/* Kontaktwege */}
      <section className="bg-white py-16 md:py-20">
        <div className="container-page grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <a
            href={company.phoneLink}
            className="group flex flex-col rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
            data-testid="kontakt-phone"
          >
            <Phone className="h-6 w-6 text-yellow" />
            <span className="mt-4 text-xs font-semibold uppercase tracking-widest text-mid-gray">Telefon</span>
            <span className="mt-1 font-heading text-lg font-bold text-anthracite">{company.phoneDisplay}</span>
          </a>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
            data-testid="kontakt-whatsapp"
          >
            <MessageCircle className="h-6 w-6 text-yellow" />
            <span className="mt-4 text-xs font-semibold uppercase tracking-widest text-mid-gray">WhatsApp</span>
            <span className="mt-1 font-heading text-lg font-bold text-anthracite">Nachricht senden</span>
          </a>
          <a
            href={company.emailLink}
            className="group flex flex-col rounded-sm border border-light-gray bg-light-gray p-6 transition-colors hover:border-yellow hover:bg-white"
            data-testid="kontakt-email"
          >
            <Mail className="h-6 w-6 text-yellow" />
            <span className="mt-4 text-xs font-semibold uppercase tracking-widest text-mid-gray">E-Mail</span>
            <span className="mt-1 break-all font-heading text-base font-bold text-anthracite">{company.email}</span>
          </a>
          <div className="flex flex-col rounded-sm border border-light-gray bg-light-gray p-6">
            <MapPin className="h-6 w-6 text-yellow" />
            <span className="mt-4 text-xs font-semibold uppercase tracking-widest text-mid-gray">Anschrift</span>
            <span className="mt-1 font-heading text-sm font-bold text-anthracite">
              Thomas Dietrich – Rümpeltaxi<br />{company.street}<br />{company.postal} {company.city}
            </span>
          </div>
        </div>
      </section>

      {/* Formular */}
      <section className="bg-light-gray py-16 md:py-24" data-testid="kontakt-form-section">
        <div className="container-page grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <Reveal>
            <span className="kicker">Anfrageformular</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl">
              Unverbindlich anfragen
            </h2>
            <p className="mt-6 text-base leading-relaxed text-mid-gray">
              Füllen Sie die Pflichtfelder aus. Je mehr Angaben Sie machen, desto genauer können wir
              Ihr Anliegen einschätzen. Wir behandeln Ihre Angaben vertraulich.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-yellow" />
                <p className="text-sm text-mid-gray">
                  Wir melden uns persönlich bei Ihnen – in der Regel zeitnah nach Eingang Ihrer Anfrage.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-yellow" />
                <p className="text-sm text-mid-gray">
                  Rümpeltaxi ist regional und überregional im Einsatz. Einsatzgebiete u. a.: {regionHubs.map((r) => r.short).join(", ")} sowie weitere Orte auf Anfrage. Bitte vereinbaren Sie vor einem persönlichen Besuch einen Termin.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div className="rounded-sm border border-light-gray bg-white p-6 md:p-8">
              <ContactForm variant="private" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
